import React, { useEffect, useState } from 'react';
import { MainLayout } from '@/components/Layout';
import { stock, products as productsApi } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Package, ArrowUpDown, ClipboardList, Factory, FileText, BarChart3, Trash2, ArrowDown, ArrowUp, AlertTriangle, ShoppingCart, ClipboardCheck, Search, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const BASE_UNITS = [
  { value: 'G', label: 'Grama (G)' },
  { value: 'ML', label: 'Mililitro (ML)' },
  { value: 'UN', label: 'Unidade (UN)' },
];

// ===================== STOCK ITEMS TAB =====================
function StockItemsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', base_unit: 'G', min_stock: '0', sku: '' });
  const [loading, setLoading] = useState(true);

  const loadItems = async () => {
    try { const r = await stock.items.list(); setItems(r.data); } catch { toast.error('Erro ao carregar itens'); } finally { setLoading(false); }
  };
  useEffect(() => { loadItems(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await stock.items.create({ name: form.name, base_unit: form.base_unit, min_stock: parseFloat(form.min_stock) || 0, sku: form.sku || null });
      toast.success('Item criado!'); setOpen(false); setForm({ name: '', base_unit: 'G', min_stock: '0', sku: '' }); loadItems();
    } catch { toast.error('Erro ao criar item'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este item?')) return;
    try { await stock.items.delete(id); toast.success('Item excluído!'); loadItems(); } catch { toast.error('Erro ao excluir'); }
  };

  return (
    <div className="space-y-4" data-testid="stock-items-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-slate-800">Itens de Estoque</h3>
        <Button onClick={() => setOpen(true)} className="bg-[#FF6B6B] hover:bg-[#FF5252]" data-testid="new-stock-item-btn">
          <Plus size={16} className="mr-2" /> Novo Item
        </Button>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-bold">Nome</TableHead>
              <TableHead className="font-bold">Unidade</TableHead>
              <TableHead className="font-bold text-right">Saldo</TableHead>
              <TableHead className="font-bold text-right">Custo Médio</TableHead>
              <TableHead className="font-bold text-right">Valor Total</TableHead>
              <TableHead className="font-bold text-right">Estoque Mín.</TableHead>
              <TableHead className="font-bold text-center">Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length > 0 ? items.map(item => {
              const totalValue = item.balance_qty * item.avg_cost;
              const belowMin = item.balance_qty < item.min_stock;
              return (
                <TableRow key={item.id} data-testid={`stock-item-${item.id}`}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell><span className="px-2 py-1 rounded bg-slate-100 text-xs font-mono">{item.base_unit}</span></TableCell>
                  <TableCell className="text-right font-semibold">{item.balance_qty.toLocaleString('pt-BR')}</TableCell>
                  <TableCell className="text-right">{formatCurrency(item.avg_cost)}</TableCell>
                  <TableCell className="text-right font-semibold">{formatCurrency(totalValue)}</TableCell>
                  <TableCell className="text-right">{item.min_stock.toLocaleString('pt-BR')}</TableCell>
                  <TableCell className="text-center">
                    <span className={cn("px-2 py-1 rounded text-xs font-semibold", belowMin ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700')}>
                      {belowMin ? 'BAIXO' : 'OK'}
                    </span>
                  </TableCell>
                  <TableCell><Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="hover:bg-red-50 hover:text-red-600"><Trash2 size={16} /></Button></TableCell>
                </TableRow>
              );
            }) : (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">{loading ? 'Carregando...' : 'Nenhum item cadastrado'}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Novo Item de Estoque</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Nome *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="bg-white border-slate-300 mt-1" data-testid="stock-item-name" /></div>
            <div><Label>SKU</Label><Input value={form.sku} onChange={e => setForm({...form, sku: e.target.value})} className="bg-white border-slate-300 mt-1" /></div>
            <div><Label>Unidade Base *</Label>
              <Select value={form.base_unit} onValueChange={v => setForm({...form, base_unit: v})}>
                <SelectTrigger className="bg-white border-slate-300 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{BASE_UNITS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Estoque Mínimo</Label><Input type="number" value={form.min_stock} onChange={e => setForm({...form, min_stock: e.target.value})} className="bg-white border-slate-300 mt-1" /></div>
            <Button type="submit" className="w-full bg-slate-700 hover:bg-slate-800">Salvar</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== MOVEMENTS TAB =====================
function MovementsTab() {
  const [movements, setMovements] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [form, setForm] = useState({ stock_item_id: '', movement_type: 'ENTRADA', qty_base: '', unit_cost_base: '', reference_type: 'NF_ENTRADA', notes: '' });
  const [manualForm, setManualForm] = useState({ stock_item_id: '', location_id: '', quantity: '', unit_cost: '', supplier_name: '', document_number: '', notes: '' });
  const [manualEntries, setManualEntries] = useState([]);
  const [showManualList, setShowManualList] = useState(false);

  const loadData = async () => {
    try { 
      const [mr, ir, lr] = await Promise.all([
        stock.movements.list({ limit: 50 }), 
        stock.items.list(),
        stock.locations.list()
      ]); 
      setMovements(mr.data); 
      setItems(ir.data);
      setLocations(lr.data);
    } catch { toast.error('Erro ao carregar'); }
  };
  
  const loadManualEntries = async () => {
    try {
      const r = await stock.manualEntry.list();
      setManualEntries(r.data);
    } catch { toast.error('Erro ao carregar entradas manuais'); }
  };
  
  useEffect(() => { loadData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const qty = parseFloat(form.qty_base);
    const cost = parseFloat(form.unit_cost_base) || 0;
    try {
      await stock.movements.create({
        stock_item_id: form.stock_item_id, movement_type: form.movement_type,
        qty_base: form.movement_type === 'SAIDA' ? -Math.abs(qty) : qty,
        unit_cost_base: cost, reference_type: form.reference_type, notes: form.notes
      });
      toast.success('Movimento registrado!'); setOpen(false); loadData();
    } catch { toast.error('Erro ao registrar movimento'); }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    try {
      await stock.manualEntry.create({
        stock_item_id: manualForm.stock_item_id,
        location_id: manualForm.location_id || locations[0]?.id,
        quantity: parseFloat(manualForm.quantity),
        unit_cost: parseFloat(manualForm.unit_cost),
        supplier_name: manualForm.supplier_name || null,
        document_number: manualForm.document_number || null,
        notes: manualForm.notes || null
      });
      toast.success('Entrada manual registrada!');
      setManualOpen(false);
      setManualForm({ stock_item_id: '', location_id: '', quantity: '', unit_cost: '', supplier_name: '', document_number: '', notes: '' });
      loadData();
    } catch (err) {
      toast.error('Erro ao registrar entrada manual');
    }
  };

  return (
    <div className="space-y-4" data-testid="stock-movements-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-slate-800">Movimentações</h3>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => { loadManualEntries(); setShowManualList(!showManualList); }} 
            className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
            data-testid="toggle-manual-list-btn"
          >
            <FileText size={16} className="mr-2" /> {showManualList ? 'Ocultar' : 'Ver'} Entradas Manuais
          </Button>
          <Button 
            onClick={() => setManualOpen(true)} 
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="new-manual-entry-btn"
          >
            <ShoppingCart size={16} className="mr-2" /> Nova Entrada Manual
          </Button>
          <Button onClick={() => setOpen(true)} className="bg-[#FF6B6B] hover:bg-[#FF5252]" data-testid="new-movement-btn">
            <Plus size={16} className="mr-2" /> Nova Movimentação
          </Button>
        </div>
      </div>
      
      {/* Lista de Entradas Manuais */}
      {showManualList && (
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
          <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
            <ShoppingCart size={18} /> Entradas Manuais (Compras sem NF)
          </h4>
          {manualEntries.length > 0 ? (
            <div className="bg-white rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-emerald-100">
                    <TableHead className="font-bold">Data</TableHead>
                    <TableHead className="font-bold">Item</TableHead>
                    <TableHead className="font-bold text-right">Qtd</TableHead>
                    <TableHead className="font-bold text-right">Custo Unit.</TableHead>
                    <TableHead className="font-bold text-right">Total</TableHead>
                    <TableHead className="font-bold">Observações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {manualEntries.map(e => (
                    <TableRow key={e.id}>
                      <TableCell className="text-sm">{new Date(e.created_at).toLocaleString('pt-BR')}</TableCell>
                      <TableCell className="font-medium">{e.stock_item_name}</TableCell>
                      <TableCell className="text-right text-emerald-700 font-semibold">+{e.quantity.toLocaleString('pt-BR')}</TableCell>
                      <TableCell className="text-right">{formatCurrency(e.unit_cost)}</TableCell>
                      <TableCell className="text-right font-semibold text-emerald-700">{formatCurrency(e.total_cost)}</TableCell>
                      <TableCell className="text-sm text-slate-500">{e.notes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-emerald-600 text-sm">Nenhuma entrada manual registrada.</p>
          )}
        </div>
      )}
      
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-bold">Data</TableHead>
              <TableHead className="font-bold">Item</TableHead>
              <TableHead className="font-bold">Tipo</TableHead>
              <TableHead className="font-bold text-right">Qtd</TableHead>
              <TableHead className="font-bold text-right">Custo Unit.</TableHead>
              <TableHead className="font-bold text-right">Total</TableHead>
              <TableHead className="font-bold">Referência</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {movements.map(m => (
              <TableRow key={m.id}>
                <TableCell className="text-sm">{new Date(m.created_at).toLocaleString('pt-BR')}</TableCell>
                <TableCell className="font-medium">{m.stock_item_name}</TableCell>
                <TableCell>
                  <span className={cn("px-2 py-1 rounded text-xs font-semibold",
                    m.movement_type === 'ENTRADA' ? 'bg-emerald-100 text-emerald-700' :
                    m.movement_type === 'SAIDA' ? 'bg-red-100 text-red-700' :
                    m.movement_type === 'AJUSTE' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                  )}>
                    {m.qty_base > 0 ? <ArrowUp size={12} className="inline mr-1" /> : <ArrowDown size={12} className="inline mr-1" />}
                    {m.movement_type}
                  </span>
                </TableCell>
                <TableCell className={cn("text-right font-semibold", m.qty_base >= 0 ? 'text-emerald-700' : 'text-red-700')}>
                  {m.qty_base > 0 ? '+' : ''}{m.qty_base.toLocaleString('pt-BR')} {m.base_unit || ''}
                </TableCell>
                <TableCell className="text-right">{formatCurrency(m.unit_cost_base)}</TableCell>
                <TableCell className={cn("text-right font-semibold", m.total_cost >= 0 ? 'text-emerald-700' : 'text-red-700')}>{formatCurrency(Math.abs(m.total_cost))}</TableCell>
                <TableCell className="text-sm text-slate-500">{m.reference_type}{m.notes ? ` - ${m.notes}` : ''}</TableCell>
              </TableRow>
            ))}
            {movements.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Nenhuma movimentação</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Nova Movimentação</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Item *</Label>
              <Select value={form.stock_item_id} onValueChange={v => setForm({...form, stock_item_id: v})}>
                <SelectTrigger className="bg-white border-slate-300 mt-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{items.map(i => <SelectItem key={i.id} value={i.id}>{i.name} ({i.base_unit})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Tipo *</Label>
              <Select value={form.movement_type} onValueChange={v => setForm({...form, movement_type: v, reference_type: v === 'ENTRADA' ? 'NF_ENTRADA' : v === 'SAIDA' ? 'VENDA' : 'AJUSTE'})}>
                <SelectTrigger className="bg-white border-slate-300 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ENTRADA">Entrada</SelectItem>
                  <SelectItem value="SAIDA">Saída</SelectItem>
                  <SelectItem value="AJUSTE">Ajuste</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Quantidade *</Label><Input type="number" step="0.01" value={form.qty_base} onChange={e => setForm({...form, qty_base: e.target.value})} required className="bg-white border-slate-300 mt-1" /></div>
              <div><Label>Custo unitário</Label><Input type="number" step="0.0001" value={form.unit_cost_base} onChange={e => setForm({...form, unit_cost_base: e.target.value})} className="bg-white border-slate-300 mt-1" /></div>
            </div>
            <div><Label>Observação</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="bg-white border-slate-300 mt-1" /></div>
            <Button type="submit" className="w-full bg-slate-700 hover:bg-slate-800">Registrar</Button>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Modal Entrada Manual */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <ShoppingCart size={20} /> Nova Entrada Manual (sem NF)
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <div>
              <Label>Item de Estoque *</Label>
              <Select value={manualForm.stock_item_id} onValueChange={v => setManualForm({...manualForm, stock_item_id: v})}>
                <SelectTrigger className="bg-white border-slate-300 mt-1" data-testid="manual-item-select">
                  <SelectValue placeholder="Selecione o item" />
                </SelectTrigger>
                <SelectContent>
                  {items.map(i => <SelectItem key={i.id} value={i.id}>{i.name} ({i.base_unit})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Quantidade *</Label>
                <Input 
                  type="number" 
                  step="0.01" 
                  value={manualForm.quantity} 
                  onChange={e => setManualForm({...manualForm, quantity: e.target.value})} 
                  required 
                  placeholder="Ex: 1000"
                  className="bg-white border-slate-300 mt-1" 
                  data-testid="manual-quantity-input"
                />
              </div>
              <div>
                <Label>Custo Unitário (R$) *</Label>
                <Input 
                  type="number" 
                  step="0.0001" 
                  value={manualForm.unit_cost} 
                  onChange={e => setManualForm({...manualForm, unit_cost: e.target.value})} 
                  required 
                  placeholder="Ex: 0.05"
                  className="bg-white border-slate-300 mt-1" 
                  data-testid="manual-cost-input"
                />
              </div>
            </div>
            <div>
              <Label>Fornecedor</Label>
              <Input 
                value={manualForm.supplier_name} 
                onChange={e => setManualForm({...manualForm, supplier_name: e.target.value})} 
                placeholder="Nome do fornecedor"
                className="bg-white border-slate-300 mt-1" 
                data-testid="manual-supplier-input"
              />
            </div>
            <div>
              <Label>Documento/Recibo</Label>
              <Input 
                value={manualForm.document_number} 
                onChange={e => setManualForm({...manualForm, document_number: e.target.value})} 
                placeholder="Número do documento (opcional)"
                className="bg-white border-slate-300 mt-1" 
              />
            </div>
            <div>
              <Label>Observações</Label>
              <Input 
                value={manualForm.notes} 
                onChange={e => setManualForm({...manualForm, notes: e.target.value})} 
                placeholder="Detalhes da compra..."
                className="bg-white border-slate-300 mt-1" 
                data-testid="manual-notes-input"
              />
            </div>
            {manualForm.quantity && manualForm.unit_cost && (
              <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                <p className="text-sm text-emerald-700">
                  <strong>Total:</strong> {formatCurrency(parseFloat(manualForm.quantity || 0) * parseFloat(manualForm.unit_cost || 0))}
                </p>
              </div>
            )}
            <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="manual-submit-btn">
              <ShoppingCart size={16} className="mr-2" /> Registrar Entrada
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== RECIPES TAB =====================
function RecipesTab() {
  const [recipes, setRecipes] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product_id: '', yield_qty: '1', yield_unit: 'UN', notes: '', ingredients: [] });

  const loadData = async () => {
    try {
      const [rr, pr, sr] = await Promise.all([stock.recipes.list(), productsApi.list(), stock.items.list()]);
      setRecipes(rr.data); setProductsList(pr.data); setStockItems(sr.data);
    } catch { toast.error('Erro ao carregar'); }
  };
  useEffect(() => { loadData(); }, []);

  const addIngredient = () => {
    setForm(prev => ({...prev, ingredients: [...prev.ingredients, { stock_item_id: '', qty_base: '', waste_factor: '0' }]}));
  };
  const removeIngredient = (idx) => {
    setForm(prev => ({...prev, ingredients: prev.ingredients.filter((_, i) => i !== idx)}));
  };
  const updateIngredient = (idx, field, value) => {
    setForm(prev => {
      const ings = [...prev.ingredients];
      ings[idx] = {...ings[idx], [field]: value};
      return {...prev, ingredients: ings};
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await stock.recipes.create({
        product_id: form.product_id, yield_qty: parseFloat(form.yield_qty), yield_unit: form.yield_unit,
        notes: form.notes,
        ingredients: form.ingredients.map(i => ({ stock_item_id: i.stock_item_id, qty_base: parseFloat(i.qty_base), waste_factor: parseFloat(i.waste_factor) || 0 }))
      });
      toast.success('Ficha técnica criada!'); setOpen(false); loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao criar ficha'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir ficha técnica?')) return;
    try { await stock.recipes.delete(id); toast.success('Excluída!'); loadData(); } catch { toast.error('Erro ao excluir'); }
  };

  return (
    <div className="space-y-4" data-testid="stock-recipes-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-slate-800">Fichas Técnicas / Receitas</h3>
        <Button onClick={() => { setForm({ product_id: '', yield_qty: '1', yield_unit: 'UN', notes: '', ingredients: [] }); setOpen(true); }} className="bg-[#FF6B6B] hover:bg-[#FF5252]" data-testid="new-recipe-btn">
          <Plus size={16} className="mr-2" /> Nova Ficha
        </Button>
      </div>
      <div className="grid gap-4">
        {recipes.map(recipe => (
          <div key={recipe.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-5" data-testid={`recipe-${recipe.id}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h4 className="text-lg font-bold text-slate-900">{recipe.product_name}</h4>
                <p className="text-sm text-slate-500">Rendimento: {recipe.yield_qty} {recipe.yield_unit} | Custo total: {formatCurrency(recipe.total_cost)} | Custo unit.: {formatCurrency(recipe.unit_cost)}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => handleDelete(recipe.id)} className="hover:bg-red-50 hover:text-red-600"><Trash2 size={16} /></Button>
            </div>
            {recipe.ingredients.length > 0 && (
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead>Insumo</TableHead><TableHead>Unidade</TableHead><TableHead className="text-right">Qtd</TableHead>
                  <TableHead className="text-right">Desperdício</TableHead><TableHead className="text-right">Custo Unit.</TableHead><TableHead className="text-right">Custo Total</TableHead>
                </TableRow></TableHeader>
                <TableBody>{recipe.ingredients.map(ing => (
                  <TableRow key={ing.id}>
                    <TableCell className="font-medium">{ing.stock_item_name}</TableCell>
                    <TableCell>{ing.base_unit}</TableCell>
                    <TableCell className="text-right">{ing.qty_base}</TableCell>
                    <TableCell className="text-right">{(ing.waste_factor * 100).toFixed(1)}%</TableCell>
                    <TableCell className="text-right">{formatCurrency(ing.unit_cost)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(ing.total_cost)}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            )}
          </div>
        ))}
        {recipes.length === 0 && <div className="text-center py-12 text-slate-500 bg-white rounded-xl border border-slate-200">Nenhuma ficha técnica cadastrada</div>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nova Ficha Técnica</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Produto *</Label>
              <Select value={form.product_id} onValueChange={v => setForm({...form, product_id: v})}>
                <SelectTrigger className="bg-white border-slate-300 mt-1"><SelectValue placeholder="Selecione o produto" /></SelectTrigger>
                <SelectContent>{productsList.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Rendimento</Label><Input type="number" step="0.01" value={form.yield_qty} onChange={e => setForm({...form, yield_qty: e.target.value})} className="bg-white border-slate-300 mt-1" /></div>
              <div><Label>Unidade</Label>
                <Select value={form.yield_unit} onValueChange={v => setForm({...form, yield_unit: v})}>
                  <SelectTrigger className="bg-white border-slate-300 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{BASE_UNITS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-base font-semibold">Ingredientes</Label>
                <Button type="button" variant="outline" size="sm" onClick={addIngredient} className="border-slate-300"><Plus size={14} className="mr-1" /> Adicionar</Button>
              </div>
              {form.ingredients.map((ing, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_80px_80px_32px] gap-2 items-end">
                  <div><Label className="text-xs">Insumo</Label>
                    <Select value={ing.stock_item_id} onValueChange={v => updateIngredient(idx, 'stock_item_id', v)}>
                      <SelectTrigger className="bg-white border-slate-300 mt-1 h-9 text-sm"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>{stockItems.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.base_unit})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Qtd base</Label><Input type="number" step="0.01" value={ing.qty_base} onChange={e => updateIngredient(idx, 'qty_base', e.target.value)} className="bg-white border-slate-300 mt-1 h-9 text-sm" /></div>
                  <div><Label className="text-xs">Perda %</Label><Input type="number" step="0.01" value={ing.waste_factor} onChange={e => updateIngredient(idx, 'waste_factor', e.target.value)} className="bg-white border-slate-300 mt-1 h-9 text-sm" /></div>
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeIngredient(idx)} className="h-9 w-9 hover:text-red-600"><Trash2 size={14} /></Button>
                </div>
              ))}
            </div>
            <Button type="submit" className="w-full bg-slate-700 hover:bg-slate-800">Salvar Ficha Técnica</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== PRODUCTION TAB =====================
function ProductionTab() {
  const [orders, setOrders] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ recipe_id: '', qty_produced: '' });

  const loadData = async () => {
    try { const [or, rr] = await Promise.all([stock.production.list(), stock.recipes.list()]); setOrders(or.data); setRecipes(rr.data); } catch { toast.error('Erro ao carregar'); }
  };
  useEffect(() => { loadData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await stock.production.create({ recipe_id: form.recipe_id, qty_produced: parseFloat(form.qty_produced) });
      toast.success('Ordem de produção criada! Estoque atualizado.'); setOpen(false); loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao criar ordem'); }
  };

  return (
    <div className="space-y-4" data-testid="stock-production-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-slate-800">Ordens de Produção</h3>
        <Button onClick={() => { setForm({ recipe_id: '', qty_produced: '' }); setOpen(true); }} className="bg-[#FF6B6B] hover:bg-[#FF5252]" data-testid="new-production-btn">
          <Plus size={16} className="mr-2" /> Nova Produção
        </Button>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead className="font-bold">Data</TableHead><TableHead className="font-bold">Produto</TableHead>
            <TableHead className="font-bold text-right">Qtd</TableHead><TableHead className="font-bold text-right">Custo Total</TableHead>
            <TableHead className="font-bold text-right">Custo Unit.</TableHead><TableHead className="font-bold text-center">Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {orders.map(po => (
              <TableRow key={po.id}>
                <TableCell className="text-sm">{new Date(po.created_at).toLocaleString('pt-BR')}</TableCell>
                <TableCell className="font-medium">{po.product_name}</TableCell>
                <TableCell className="text-right">{po.qty_produced}</TableCell>
                <TableCell className="text-right">{formatCurrency(po.total_cost)}</TableCell>
                <TableCell className="text-right">{formatCurrency(po.unit_cost)}</TableCell>
                <TableCell className="text-center"><span className={cn("px-2 py-1 rounded text-xs font-semibold",
                  po.status === 'CONCLUIDO' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
                )}>{po.status}</span></TableCell>
              </TableRow>
            ))}
            {orders.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Nenhuma ordem de produção</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Nova Ordem de Produção</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Ficha Técnica *</Label>
              <Select value={form.recipe_id} onValueChange={v => setForm({...form, recipe_id: v})}>
                <SelectTrigger className="bg-white border-slate-300 mt-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{recipes.map(r => <SelectItem key={r.id} value={r.id}>{r.product_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Quantidade a Produzir *</Label><Input type="number" step="0.01" value={form.qty_produced} onChange={e => setForm({...form, qty_produced: e.target.value})} required className="bg-white border-slate-300 mt-1" /></div>
            <Button type="submit" className="w-full bg-slate-700 hover:bg-slate-800">Iniciar Produção</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== NF ENTRADA TAB =====================
function FiscalTab() {
  const [docs, setDocs] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ number: '', supplier_name: '', supplier_cnpj: '', items: [] });

  const loadData = async () => {
    try { const [dr, sr] = await Promise.all([stock.fiscal.list(), stock.items.list()]); setDocs(dr.data); setStockItems(sr.data); } catch { toast.error('Erro ao carregar'); }
  };
  useEffect(() => { loadData(); }, []);

  const addItem = () => {
    setForm(prev => ({...prev, items: [...prev.items, { stock_item_id: '', description: '', quantity: '', unit: 'KG', unit_price: '', total_price: '' }]}));
  };
  const removeItem = (idx) => { setForm(prev => ({...prev, items: prev.items.filter((_, i) => i !== idx)})); };
  const updateItem = (idx, field, value) => {
    setForm(prev => {
      const items = [...prev.items];
      items[idx] = {...items[idx], [field]: value};
      if (field === 'quantity' || field === 'unit_price') {
        const qty = parseFloat(items[idx].quantity) || 0;
        const price = parseFloat(items[idx].unit_price) || 0;
        items[idx].total_price = (qty * price).toFixed(2);
      }
      return {...prev, items};
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const totalValue = form.items.reduce((s, i) => s + (parseFloat(i.total_price) || 0), 0);
    try {
      await stock.fiscal.create({
        number: form.number, supplier_name: form.supplier_name, supplier_cnpj: form.supplier_cnpj,
        total_value: totalValue,
        items: form.items.map(i => ({
          stock_item_id: i.stock_item_id || null, description: i.description,
          quantity: parseFloat(i.quantity), unit: i.unit,
          unit_price: parseFloat(i.unit_price), total_price: parseFloat(i.total_price)
        }))
      });
      toast.success('NF registrada! Estoque e contas a pagar atualizados.'); setOpen(false); loadData();
    } catch { toast.error('Erro ao registrar NF'); }
  };

  return (
    <div className="space-y-4" data-testid="stock-fiscal-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-slate-800">Notas Fiscais de Entrada</h3>
        <Button onClick={() => { setForm({ number: '', supplier_name: '', supplier_cnpj: '', items: [] }); setOpen(true); }} className="bg-[#FF6B6B] hover:bg-[#FF5252]" data-testid="new-fiscal-btn">
          <Plus size={16} className="mr-2" /> Nova NF Entrada
        </Button>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead className="font-bold">Data</TableHead><TableHead className="font-bold">Número</TableHead>
            <TableHead className="font-bold">Fornecedor</TableHead><TableHead className="font-bold text-right">Valor Total</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {docs.map(d => (
              <TableRow key={d.id}><TableCell className="text-sm">{new Date(d.created_at).toLocaleString('pt-BR')}</TableCell><TableCell>{d.number || '-'}</TableCell><TableCell>{d.supplier_name || '-'}</TableCell><TableCell className="text-right font-semibold">{formatCurrency(d.total_value)}</TableCell></TableRow>
            ))}
            {docs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-500">Nenhuma NF registrada</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nova NF de Entrada</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Número NF</Label><Input value={form.number} onChange={e => setForm({...form, number: e.target.value})} className="bg-white border-slate-300 mt-1" /></div>
              <div><Label>Fornecedor</Label><Input value={form.supplier_name} onChange={e => setForm({...form, supplier_name: e.target.value})} className="bg-white border-slate-300 mt-1" /></div>
              <div><Label>CNPJ</Label><Input value={form.supplier_cnpj} onChange={e => setForm({...form, supplier_cnpj: e.target.value})} className="bg-white border-slate-300 mt-1" /></div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center"><Label className="text-base font-semibold">Itens da NF</Label><Button type="button" variant="outline" size="sm" onClick={addItem} className="border-slate-300"><Plus size={14} className="mr-1" />Adicionar Item</Button></div>
              {form.items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_120px_70px_70px_90px_90px_32px] gap-2 items-end border-b border-slate-100 pb-2">
                  <div><Label className="text-xs">Item estoque</Label>
                    <Select value={item.stock_item_id} onValueChange={v => { updateItem(idx, 'stock_item_id', v); const si = stockItems.find(s => s.id === v); if(si) updateItem(idx, 'description', si.name); }}>
                      <SelectTrigger className="bg-white border-slate-300 mt-1 h-9 text-sm"><SelectValue placeholder="Vincular" /></SelectTrigger>
                      <SelectContent>{stockItems.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Descrição</Label><Input value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} className="bg-white border-slate-300 mt-1 h-9 text-sm" /></div>
                  <div><Label className="text-xs">Qtd</Label><Input type="number" step="0.01" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="bg-white border-slate-300 mt-1 h-9 text-sm" /></div>
                  <div><Label className="text-xs">Un.</Label><Input value={item.unit} onChange={e => updateItem(idx, 'unit', e.target.value)} className="bg-white border-slate-300 mt-1 h-9 text-sm" /></div>
                  <div><Label className="text-xs">Preço un.</Label><Input type="number" step="0.01" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} className="bg-white border-slate-300 mt-1 h-9 text-sm" /></div>
                  <div><Label className="text-xs">Total</Label><Input value={item.total_price} readOnly className="bg-slate-50 border-slate-300 mt-1 h-9 text-sm" /></div>
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} className="h-9 w-9 hover:text-red-600"><Trash2 size={14} /></Button>
                </div>
              ))}
            </div>
            <div className="text-right text-lg font-bold text-slate-900">Total: {formatCurrency(form.items.reduce((s, i) => s + (parseFloat(i.total_price) || 0), 0))}</div>
            <Button type="submit" className="w-full bg-slate-700 hover:bg-slate-800">Registrar NF</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== REPORTS TAB =====================
function ReportsTab() {
  const [report, setReport] = useState(null);
  const [reportType, setReportType] = useState('cmv');
  const [days, setDays] = useState('30');
  const [loading, setLoading] = useState(false);
  const [reportError, setReportError] = useState(null);

  const loadReport = async () => {
    setReport(null);
    setReportError(null);
    setLoading(true);
    try {
      let r;
      switch (reportType) {
        case 'cmv': r = await stock.reports.cmvByProduct(days); break;
        case 'abc': r = await stock.reports.abcCurve(days); break;
        case 'status': r = await stock.reports.stockStatus(); break;
        case 'margin': r = await stock.reports.marginByPeriod(days); break;
        case 'turnover': r = await stock.reports.stockTurnover(days); break;
        default: setLoading(false); return;
      }
      setReport(r.data);
    } catch (err) {
      console.error('Erro ao carregar relatório:', err);
      setReportError(err?.response?.data?.detail || err?.message || 'Erro desconhecido');
      toast.error('Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadReport(); }, [reportType, days]);

  return (
    <div className="space-y-4" data-testid="stock-reports-tab">
      <div className="flex gap-3 items-center flex-wrap">
        <Select value={reportType} onValueChange={setReportType}>
          <SelectTrigger className="w-52 bg-white border-slate-300"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="cmv">CMV por Produto</SelectItem>
            <SelectItem value="abc">Curva ABC</SelectItem>
            <SelectItem value="status">Status do Estoque</SelectItem>
            <SelectItem value="margin">Margem por Período</SelectItem>
            <SelectItem value="turnover">Giro de Estoque</SelectItem>
          </SelectContent>
        </Select>
        {reportType !== 'status' && (
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-36 bg-white border-slate-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 dias</SelectItem>
              <SelectItem value="15">15 dias</SelectItem>
              <SelectItem value="30">30 dias</SelectItem>
              <SelectItem value="60">60 dias</SelectItem>
              <SelectItem value="90">90 dias</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Loading / Error States */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          <span className="ml-3 text-slate-500">Carregando relatório...</span>
        </div>
      )}
      {reportError && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium">Erro ao carregar relatório</p>
          <p className="text-red-400 text-sm mt-1">{reportError}</p>
          <button onClick={loadReport} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">Tentar novamente</button>
        </div>
      )}

      {/* Margin Summary */}
      {!loading && reportType === 'margin' && report && !Array.isArray(report) && (
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Receita Total', value: formatCurrency(report.total_revenue), color: 'text-slate-900' },
            { label: 'CMV Total', value: formatCurrency(report.total_cogs), color: 'text-red-600' },
            { label: 'Lucro Bruto', value: formatCurrency(report.gross_profit), color: 'text-emerald-600' },
            { label: 'Margem Bruta', value: `${report.gross_margin}%`, color: 'text-blue-600' },
          ].map((card, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <p className="text-sm text-slate-500">{card.label}</p>
              <p className={cn("text-2xl font-bold mt-1", card.color)}>{card.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Table Reports */}
      {!loading && Array.isArray(report) && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {reportType === 'cmv' && <><TableHead className="font-bold">Produto</TableHead><TableHead className="font-bold text-right">Receita</TableHead><TableHead className="font-bold text-right">CMV</TableHead><TableHead className="font-bold text-right">Lucro</TableHead><TableHead className="font-bold text-right">Margem</TableHead><TableHead className="font-bold text-right">Qtd</TableHead></>}
                {reportType === 'abc' && <><TableHead className="font-bold">Produto</TableHead><TableHead className="font-bold text-right">Receita</TableHead><TableHead className="font-bold text-right">Qtd</TableHead><TableHead className="font-bold text-right">Acumulado</TableHead><TableHead className="font-bold text-center">Classe</TableHead></>}
                {reportType === 'status' && <><TableHead className="font-bold">Item</TableHead><TableHead className="font-bold">Unidade</TableHead><TableHead className="font-bold text-right">Saldo</TableHead><TableHead className="font-bold text-right">Custo Médio</TableHead><TableHead className="font-bold text-right">Valor Total</TableHead><TableHead className="font-bold text-center">Status</TableHead></>}
                {reportType === 'turnover' && <><TableHead className="font-bold">Item</TableHead><TableHead className="font-bold">Unidade</TableHead><TableHead className="font-bold text-right">Saldo Atual</TableHead><TableHead className="font-bold text-right">Consumido</TableHead><TableHead className="font-bold text-right">Giro</TableHead><TableHead className="font-bold text-right">Dias Estoque</TableHead></>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.map((row, idx) => (
                <TableRow key={idx}>
                  {reportType === 'cmv' && <><TableCell className="font-medium">{row.product_name}</TableCell><TableCell className="text-right">{formatCurrency(row.total_revenue)}</TableCell><TableCell className="text-right text-red-600">{formatCurrency(row.total_cogs)}</TableCell><TableCell className="text-right text-emerald-600">{formatCurrency(row.gross_profit)}</TableCell><TableCell className="text-right font-semibold">{row.gross_margin}%</TableCell><TableCell className="text-right">{row.qty_sold}</TableCell></>}
                  {reportType === 'abc' && <><TableCell className="font-medium">{row.product_name}</TableCell><TableCell className="text-right">{formatCurrency(row.total_revenue)}</TableCell><TableCell className="text-right">{row.qty_sold}</TableCell><TableCell className="text-right">{row.cumulative_pct}%</TableCell><TableCell className="text-center"><span className={cn("px-3 py-1 rounded text-xs font-bold", row.classification === 'A' ? 'bg-emerald-100 text-emerald-700' : row.classification === 'B' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700')}>{row.classification}</span></TableCell></>}
                  {reportType === 'status' && <><TableCell className="font-medium">{row.stock_item_name}</TableCell><TableCell>{row.base_unit}</TableCell><TableCell className="text-right">{(row.qty_base ?? 0).toLocaleString('pt-BR')}</TableCell><TableCell className="text-right">{formatCurrency(row.avg_cost)}</TableCell><TableCell className="text-right font-semibold">{formatCurrency(row.total_value)}</TableCell><TableCell className="text-center"><span className={cn("px-2 py-1 rounded text-xs font-semibold", row.status === 'OK' ? 'bg-emerald-100 text-emerald-700' : row.status === 'BAIXO' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700')}>{row.status}</span></TableCell></>}
                  {reportType === 'turnover' && <><TableCell className="font-medium">{row.stock_item_name}</TableCell><TableCell>{row.base_unit}</TableCell><TableCell className="text-right">{(row.current_balance ?? 0).toLocaleString('pt-BR')}</TableCell><TableCell className="text-right">{(row.total_consumed ?? 0).toLocaleString('pt-BR')}</TableCell><TableCell className="text-right font-semibold">{row.turnover_ratio}x</TableCell><TableCell className="text-right">{row.days_of_stock} dias</TableCell></>}
                </TableRow>
              ))}
              {report.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Sem dados para o período</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ===================== INVENTORY TAB =====================
const ADJUSTMENT_REASONS = [
  { value: 'PERDA', label: 'Perda', color: 'text-red-600' },
  { value: 'QUEBRA', label: 'Quebra', color: 'text-orange-600' },
  { value: 'ERRO_CONTAGEM', label: 'Erro de Contagem', color: 'text-blue-600' },
  { value: 'FURTO', label: 'Furto', color: 'text-red-700' },
  { value: 'VENCIMENTO', label: 'Vencimento', color: 'text-amber-600' },
  { value: 'SOBRA', label: 'Sobra', color: 'text-emerald-600' },
  { value: 'OUTRO', label: 'Outro', color: 'text-slate-600' },
];

function InventoryTab() {
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [countingItem, setCountingItem] = useState(null);
  const [countForm, setCountForm] = useState({ counted_qty: '', adjustment_reason: 'ERRO_CONTAGEM', notes: '' });

  const loadData = async () => {
    try {
      setLoading(true);
      const [itemsRes, summaryRes] = await Promise.all([
        stock.inventory.items(),
        stock.inventory.summary(30)
      ]);
      setItems(itemsRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      toast.error('Erro ao carregar dados de inventário');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await stock.inventory.history({ limit: 100 });
      setHistory(res.data);
    } catch {
      toast.error('Erro ao carregar histórico');
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleStartCount = (item) => {
    setCountingItem(item);
    setCountForm({ counted_qty: item.current_qty.toString(), adjustment_reason: 'ERRO_CONTAGEM', notes: '' });
  };

  const handleSubmitCount = async (e) => {
    e.preventDefault();
    if (!countingItem) return;

    try {
      const response = await stock.inventory.count({
        stock_item_id: countingItem.stock_item_id,
        counted_qty: parseFloat(countForm.counted_qty),
        adjustment_reason: countForm.adjustment_reason,
        notes: countForm.notes || null
      });
      
      const diff = response.data.difference;
      if (Math.abs(diff) < 0.0001) {
        toast.success('Contagem registrada - Sem diferença!');
      } else if (diff > 0) {
        toast.success(`Contagem registrada - Ajuste: +${diff.toFixed(2)} ${countingItem.base_unit}`);
      } else {
        toast.warning(`Contagem registrada - Ajuste: ${diff.toFixed(2)} ${countingItem.base_unit}`);
      }
      
      setCountingItem(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao registrar contagem');
    }
  };

  const filteredItems = items.filter(item => 
    item.stock_item_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="stock-inventory-tab">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Contagens (30 dias)</p>
            <p className="text-2xl font-bold text-slate-900">{summary.total_counts}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Ajustes Positivos</p>
            <p className="text-2xl font-bold text-emerald-600">+{summary.positive_adjustments}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Ajustes Negativos</p>
            <p className="text-2xl font-bold text-red-600">-{summary.negative_adjustments}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Impacto Total (R$)</p>
            <p className={cn("text-2xl font-bold", summary.total_cost_impact >= 0 ? 'text-emerald-600' : 'text-red-600')}>
              {formatCurrency(summary.total_cost_impact)}
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h3 className="text-lg font-semibold text-slate-800">Contagem de Inventário</h3>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input 
              placeholder="Buscar item..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-white border-slate-300 w-56"
              data-testid="inventory-search"
            />
          </div>
          <Button 
            variant="outline" 
            onClick={() => { loadHistory(); setShowHistory(!showHistory); }} 
            className="border-slate-300"
            data-testid="toggle-history-btn"
          >
            <ClipboardCheck size={16} className="mr-2" /> {showHistory ? 'Ocultar' : 'Ver'} Histórico
          </Button>
        </div>
      </div>

      {/* History Section */}
      {showHistory && (
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
            <ClipboardCheck size={18} /> Histórico de Contagens
          </h4>
          {history.length > 0 ? (
            <div className="bg-white rounded-lg overflow-hidden max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-blue-100">
                    <TableHead className="font-bold">Data</TableHead>
                    <TableHead className="font-bold">Item</TableHead>
                    <TableHead className="font-bold text-right">Esperado</TableHead>
                    <TableHead className="font-bold text-right">Contado</TableHead>
                    <TableHead className="font-bold text-right">Diferença</TableHead>
                    <TableHead className="font-bold">Motivo</TableHead>
                    <TableHead className="font-bold text-right">Impacto</TableHead>
                    <TableHead className="font-bold">Usuário</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map(h => {
                    const reason = ADJUSTMENT_REASONS.find(r => r.value === h.adjustment_reason);
                    return (
                      <TableRow key={h.id}>
                        <TableCell className="text-sm">{new Date(h.created_at).toLocaleString('pt-BR')}</TableCell>
                        <TableCell className="font-medium">{h.stock_item_name}</TableCell>
                        <TableCell className="text-right">{h.expected_qty.toLocaleString('pt-BR')} {h.base_unit}</TableCell>
                        <TableCell className="text-right">{h.counted_qty.toLocaleString('pt-BR')} {h.base_unit}</TableCell>
                        <TableCell className={cn("text-right font-semibold", h.difference >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                          {h.difference > 0 ? '+' : ''}{h.difference.toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell>
                          <span className={cn("text-xs font-medium", reason?.color || 'text-slate-600')}>
                            {reason?.label || h.adjustment_reason}
                          </span>
                        </TableCell>
                        <TableCell className={cn("text-right font-semibold", h.cost_impact >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                          {formatCurrency(h.cost_impact)}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{h.created_by_user_name || '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-blue-600 text-sm">Nenhuma contagem registrada ainda.</p>
          )}
        </div>
      )}

      {/* Items List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-bold">Item</TableHead>
              <TableHead className="font-bold">Unidade</TableHead>
              <TableHead className="font-bold text-right">Saldo Atual</TableHead>
              <TableHead className="font-bold text-right">Custo Médio</TableHead>
              <TableHead className="font-bold text-right">Valor Total</TableHead>
              <TableHead className="font-bold text-center">Status</TableHead>
              <TableHead className="font-bold text-center">Última Contagem</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Carregando...</TableCell></TableRow>
            ) : filteredItems.length > 0 ? filteredItems.map(item => (
              <TableRow key={item.stock_item_id} data-testid={`inventory-item-${item.stock_item_id}`}>
                <TableCell className="font-medium">{item.stock_item_name}</TableCell>
                <TableCell><span className="px-2 py-1 rounded bg-slate-100 text-xs font-mono">{item.base_unit}</span></TableCell>
                <TableCell className="text-right font-semibold">{item.current_qty.toLocaleString('pt-BR')}</TableCell>
                <TableCell className="text-right">{formatCurrency(item.avg_cost)}</TableCell>
                <TableCell className="text-right font-semibold">{formatCurrency(item.total_value)}</TableCell>
                <TableCell className="text-center">
                  <span className={cn("px-2 py-1 rounded text-xs font-semibold",
                    item.status === 'OK' ? 'bg-emerald-100 text-emerald-700' :
                    item.status === 'BAIXO' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'
                  )}>
                    {item.status}
                  </span>
                </TableCell>
                <TableCell className="text-center text-sm text-slate-500">
                  {item.last_count_date ? new Date(item.last_count_date).toLocaleDateString('pt-BR') : '-'}
                </TableCell>
                <TableCell>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleStartCount(item)}
                    className="border-blue-300 text-blue-600 hover:bg-blue-50"
                    data-testid={`count-btn-${item.stock_item_id}`}
                  >
                    <ClipboardCheck size={14} className="mr-1" /> Contar
                  </Button>
                </TableCell>
              </TableRow>
            )) : (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">
                {searchTerm ? 'Nenhum item encontrado' : 'Nenhum item de estoque cadastrado'}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Counting Dialog */}
      <Dialog open={!!countingItem} onOpenChange={() => setCountingItem(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <ClipboardCheck size={20} /> Contagem de Inventário
            </DialogTitle>
          </DialogHeader>
          {countingItem && (
            <form onSubmit={handleSubmitCount} className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-semibold text-slate-900">{countingItem.stock_item_name}</p>
                <p className="text-sm text-slate-500">Saldo atual no sistema: <span className="font-bold text-slate-700">{countingItem.current_qty.toLocaleString('pt-BR')} {countingItem.base_unit}</span></p>
                <p className="text-sm text-slate-500">Custo médio: <span className="font-bold text-slate-700">{formatCurrency(countingItem.avg_cost)}</span></p>
              </div>

              <div>
                <Label>Quantidade Contada *</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={countForm.counted_qty}
                  onChange={(e) => setCountForm({...countForm, counted_qty: e.target.value})}
                  required
                  placeholder="Digite a quantidade contada"
                  className="bg-white border-slate-300 mt-1 text-lg font-semibold"
                  data-testid="counted-qty-input"
                  autoFocus
                />
              </div>

              {countForm.counted_qty && parseFloat(countForm.counted_qty) !== countingItem.current_qty && (
                <div className={cn("p-3 rounded-lg border",
                  parseFloat(countForm.counted_qty) > countingItem.current_qty 
                    ? 'bg-emerald-50 border-emerald-200' 
                    : 'bg-red-50 border-red-200'
                )}>
                  <p className={cn("text-sm font-medium",
                    parseFloat(countForm.counted_qty) > countingItem.current_qty ? 'text-emerald-700' : 'text-red-700'
                  )}>
                    Diferença: {parseFloat(countForm.counted_qty) > countingItem.current_qty ? '+' : ''}
                    {(parseFloat(countForm.counted_qty) - countingItem.current_qty).toLocaleString('pt-BR')} {countingItem.base_unit}
                  </p>
                  <p className={cn("text-xs",
                    parseFloat(countForm.counted_qty) > countingItem.current_qty ? 'text-emerald-600' : 'text-red-600'
                  )}>
                    Impacto: {formatCurrency((parseFloat(countForm.counted_qty) - countingItem.current_qty) * countingItem.avg_cost)}
                  </p>
                </div>
              )}

              <div>
                <Label>Motivo do Ajuste *</Label>
                <Select value={countForm.adjustment_reason} onValueChange={(v) => setCountForm({...countForm, adjustment_reason: v})}>
                  <SelectTrigger className="bg-white border-slate-300 mt-1" data-testid="adjustment-reason-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ADJUSTMENT_REASONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>
                        <span className={r.color}>{r.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Observações</Label>
                <Input 
                  value={countForm.notes}
                  onChange={(e) => setCountForm({...countForm, notes: e.target.value})}
                  placeholder="Detalhes adicionais..."
                  className="bg-white border-slate-300 mt-1"
                  data-testid="count-notes-input"
                />
              </div>

              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setCountingItem(null)} 
                  className="flex-1"
                >
                  <X size={16} className="mr-1" /> Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-count-btn"
                >
                  <Check size={16} className="mr-1" /> Registrar Contagem
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== MAIN PAGE =====================
export function StockPage() {
  return (
    <MainLayout>
      <div className="space-y-6 page-enter" data-testid="stock-page">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Barlow Condensed' }}>Estoque & CMV</h1>
          <p className="text-slate-600">Controle de estoque, fichas técnicas, produção e relatórios</p>
        </div>

        <Tabs defaultValue="items" className="space-y-4">
          <TabsList className="bg-white border border-slate-200 shadow-sm p-1 h-auto flex-wrap">
            <TabsTrigger value="items" className="text-slate-600 data-[state=active]:bg-[#FF6B6B] data-[state=active]:text-white px-4 py-2 text-sm font-medium">
              <Package size={16} className="mr-2" /> Itens
            </TabsTrigger>
            <TabsTrigger value="inventory" className="text-slate-600 data-[state=active]:bg-[#FF6B6B] data-[state=active]:text-white px-4 py-2 text-sm font-medium">
              <ClipboardCheck size={16} className="mr-2" /> Inventário
            </TabsTrigger>
            <TabsTrigger value="movements" className="text-slate-600 data-[state=active]:bg-[#FF6B6B] data-[state=active]:text-white px-4 py-2 text-sm font-medium">
              <ArrowUpDown size={16} className="mr-2" /> Movimentações
            </TabsTrigger>
            <TabsTrigger value="recipes" className="text-slate-600 data-[state=active]:bg-[#FF6B6B] data-[state=active]:text-white px-4 py-2 text-sm font-medium">
              <ClipboardList size={16} className="mr-2" /> Fichas Técnicas
            </TabsTrigger>
            <TabsTrigger value="production" className="text-slate-600 data-[state=active]:bg-[#FF6B6B] data-[state=active]:text-white px-4 py-2 text-sm font-medium">
              <Factory size={16} className="mr-2" /> Produção
            </TabsTrigger>
            <TabsTrigger value="fiscal" className="text-slate-600 data-[state=active]:bg-[#FF6B6B] data-[state=active]:text-white px-4 py-2 text-sm font-medium">
              <FileText size={16} className="mr-2" /> NF Entrada
            </TabsTrigger>
            <TabsTrigger value="reports" className="text-slate-600 data-[state=active]:bg-[#FF6B6B] data-[state=active]:text-white px-4 py-2 text-sm font-medium">
              <BarChart3 size={16} className="mr-2" /> Relatórios
            </TabsTrigger>
          </TabsList>

          <TabsContent value="items"><StockItemsTab /></TabsContent>
          <TabsContent value="inventory"><InventoryTab /></TabsContent>
          <TabsContent value="movements"><MovementsTab /></TabsContent>
          <TabsContent value="recipes"><RecipesTab /></TabsContent>
          <TabsContent value="production"><ProductionTab /></TabsContent>
          <TabsContent value="fiscal"><FiscalTab /></TabsContent>
          <TabsContent value="reports"><ReportsTab /></TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
